import {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
  GetManyParams,
  GetManyResult,
  DeleteParams,
} from 'react-admin'
import { PRINT_QRCODE_ITEMS_URL } from '../../api-urls'
import {
  get,
  getWithPagination,
  patch,
  post,
  remove,
} from '../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../common/ticket-api-get-list.params'
import filterMapper from './print-qrcode-items-filter.mapper'
import { mapSortPrintQRCodeItemsParam } from './print-qrcode-items-sort.mapper'
import { PrintAnchor } from '../../enum/PrintAnchor'
import { printQRCodeItemDto } from '../../dto/print-qrcode-item.dto'
import { PrintParameterTypes } from '../../enum/PrintParameterTypes'
import printItemParameterProvider from '../print-item-parameters/print-item-parameters.provider'
import { ResourceName } from '../../ResourceName'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<printQRCodeItemDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(
      sort,
      pagination,
      mapSortPrintQRCodeItemsParam,
    )
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<printQRCodeItemDto[]>(
      `${PRINT_QRCODE_ITEMS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<printQRCodeItemDto>> => {
    const data = await get<printQRCodeItemDto>(PRINT_QRCODE_ITEMS_URL, `/${id}`)
    return Promise.resolve({
      data,
    })
  },
  create: async (
    resource,
    { data }: CreateParams<CreatePrintQRCodeItemRequest>,
  ): Promise<CreateResult<printQRCodeItemDto>> => {
    const response = await post<
      CreatePrintQRCodeItemRequest[],
      printQRCodeItemDto[]
    >(PRINT_QRCODE_ITEMS_URL, [data])
    const printQRCodeItem = response[0]
    if (data.newParamKey && data.newParamType && printQRCodeItem.id)
      await printItemParameterProvider.create(
        ResourceName.PRINT_ITEM_PARAMETERS,
        {
          data: {
            key: data.newParamKey,
            options: data?.newParamOptions,
            type: data.newParamType,
            printItemId: printQRCodeItem.id,
          },
        },
      )
    return {
      data: printQRCodeItem,
    }
  },
  update: async (
    resource: string,
    { id, data }: UpdateParams,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      printQRCodeItemDto
    >(PRINT_QRCODE_ITEMS_URL, {
      id,
      ...data,
    })
    return Promise.resolve({ data: update })
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<CreateResult> => {
    const deleted = await remove<printQRCodeItemDto>(
      PRINT_QRCODE_ITEMS_URL,
      `/Delete/${id}`,
    )

    return Promise.resolve({ data: deleted })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<printQRCodeItemDto>> => {
    const data = await get<printQRCodeItemDto[]>(
      PRINT_QRCODE_ITEMS_URL,
      `/GetByConditions/o=>new int[] {${ids.toString()}}.Contains(o.Id)`,
    )
    return Promise.resolve({ data })
  },
} as DataProvider

export interface CreatePrintQRCodeItemRequest {
  readonly xPosition: number
  readonly yPosition: number
  readonly anchor: PrintAnchor
  readonly priority: number
  readonly printLayoutId: number
  readonly code: string
  readonly qrCodeCorrectionLevel: string
  readonly fontSize: number
  readonly moduleSize: number
  readonly darkHexColor: string
  readonly lightHexColor: string
  readonly addQuietZone: boolean
  readonly newParamKey: string
  readonly newParamOptions: string
  readonly newParamType: PrintParameterTypes
}

export default provider
